import { Dispatch, SetStateAction, useRef, useState } from "react";

/**
 * A custom hook that allows you to keep track of the previous states of a variable.
 * @param initialState The initial state of the variable.
 * @param depth The number of previous states to keep track of.
 * @param excludeUndefinedInitialState Whether an undefined initial state should be excluded from the array of previous states.
 * @returns A tuple containing the current state, a stack of previous states (LIFO), and a function to set the current state.
 */
function useHistoricalState<T>(
    initialState?: T | (() => T),
    depth: number = 1,
    excludeUndefinedInitialState: boolean = true
): [T, T[], Dispatch<SetStateAction<T>>] {
    const [state, setState] = useState(initialState);
    const previousStates = useRef<T[]>([]);

    function setCurrentState(newState: T): void {
        if (!excludeUndefinedInitialState || previousStates.current.length > 0 || state !== undefined) {
            const newLength = previousStates.current.unshift(state);
            if (newLength > depth)
                previousStates.current.splice(-1);
        }

        setState(newState);
    }

    return [state, previousStates.current, setCurrentState];
}

export { useHistoricalState };
